import { LEGAL_PERSON } from 'data-layer-cr/dist/constants/customerTypes';
import { defaultUnit } from 'data-layer-cr/dist/functions/quotations';
import {
  GeneralCatalogItem,
  Quotation,
  QuotationCoverage,
  QuotationUnit,
} from 'data-layer-cr/dist/models';
import { dateFormat } from 'design-system';

// Methods
import driverAndCustomerAreDifferent from 'src/functions/driverAndCustomerAreDifferent';

// Models
import { getDeductibleDefaultBase } from 'src/functions/coverageMethods';
import CarItem from '../helperTypes/CarItem';
import CustomerItem, {
  makeEmptyCustomerItem,
  NATURAL_PERSON,
} from '../helperTypes/CustomerItem';

// Request sub components
import PricingRequestCoverage from './subEntities/PricingRequestCoverage';

// Request types
import ModelsCarItem from '../Catalogs/ModelsCarItem';
import PricingCreationRequest from './PricingCreationRequest';
import PricingRequest from './PricingPreviewRequest';

// Data treatment methods
import {
  brandNameForId,
  modelNameForId,
} from '../../functions/catalogueMapMethods';
import setInsuredValue from '../../functions/setInsuredValue';
import { makeRequestCustomer, makeRequestUnitData } from './functions';

// Constants
import {
  THIRD_PARTY_PACK,
  THIRD_PARTY_VEHICLE,
} from '../../constants/products';
import PricingRequestUnit from './subEntities/PricingRequestUnit';

const { digestDateForBackend, dateFromServerString } = dateFormat;

/**
 * Makes a pricing preview request, used to query the pricing without actually
 * creating it in the database
 *
 * @param customer - The customer data which right now encapsulates both driver and customer data
 * @param car - The car data
 * @param coverages - The coverages to apply
 */
export function makePricingPreviewRequest(
  customer: CustomerItem,
  car: CarItem,
  productId: string,
  coverages: QuotationCoverage[] = [],
  deductOption?: number,
  profileId?: number,
  hasMultipleOptions = false,
): PricingRequest {
  const requestCoverages: PricingRequestCoverage[] = [];
  coverages.forEach((coverage: QuotationCoverage): void => {
    requestCoverages.push({
      coverageId: coverage.coverageId,
      insuredValue: coverage.insuredValue,
      coreBranch: coverage.coreBranch || '02B',
    });
  });

  const requestDriver = makeRequestCustomer(customer);
  const requestUnitData = makeRequestUnitData(car, requestDriver);

  return {
    productId,
    discountPrice: productId === THIRD_PARTY_VEHICLE ? 40 : 0,
    hasMultipleOptions,
    units: [
      {
        deductibleOption: deductOption !== undefined ? Number(deductOption) : 1,
        Unit: requestUnitData,
        coverages: requestCoverages,
      },
    ],
    profileId: Number(profileId),
  };
}

type MakeUpdatePricingProps = {
  customer: CustomerItem;
  car: CarItem;
  productId: string;
  modifiedPackage: number;
  units: QuotationUnit[];
  coverages: QuotationCoverage[];
  hasMultipleOptions: boolean;
  deductOption?: number;
  profileId?: number;
  packageSelected?: string;
};

export function makeUpdatePricingRequest({
  customer,
  car,
  productId,
  modifiedPackage,
  units,
  coverages = [],
  deductOption,
  profileId,
  hasMultipleOptions = false,
  packageSelected,
}: MakeUpdatePricingProps): PricingRequest {
  const requestDriver = makeRequestCustomer(customer);
  const requestUnitData = makeRequestUnitData(car, requestDriver);

  const newUnits: PricingRequestUnit[] = [];
  units.forEach((unit, index) => {
    const selectecCoverages =
      index === modifiedPackage ? coverages : unit.coverages;
    const newCoverages: PricingRequestCoverage[] = selectecCoverages.map(
      (coverage) => {
        const pricingCoverage: PricingRequestCoverage = {
          coverageId: coverage.coverageId,
          insuredValue: setInsuredValue(
            coverage.insuredValue,
            coverage.coverageId,
          ),
          coreBranch: coverage.coreBranch || '02B',
        };
        if (hasMultipleOptions) {
          pricingCoverage.deductiblePercentage = coverage.deductibleRate;
          pricingCoverage.minimumDeductible = coverage.deductible;
          pricingCoverage.deductibleBase = getDeductibleDefaultBase(
            index + 1,
            coverage.coverageId,
          );
        }
        return pricingCoverage;
      },
    );
    const newUnit: PricingRequestUnit = {
      deductibleOption: deductOption !== undefined ? Number(deductOption) : 1,
      Unit: requestUnitData,
      coverages: newCoverages,
    };
    if (hasMultipleOptions) {
      newUnit.package =
        index === modifiedPackage ? packageSelected : unit.package.description;
    }
    newUnits.push(newUnit);
  });

  return {
    productId,
    discountPrice: 0,
    hasMultipleOptions,
    units: newUnits,
    profileId: Number(profileId),
  };
}

export type MakePricingCreationRequestParams = {
  customer: CustomerItem;
  car: CarItem;
  productId: string;
  coverages: QuotationCoverage[];
  brands: GeneralCatalogItem[];
  models: ModelsCarItem[];
  license: string;
  packageSelected: number;
  units: QuotationUnit[];
  deductibleOption?: number;
  hasMultipleOptions: boolean;
  profileId?: number;
  additionalBrokers: string[];
  subagentlicense: string;
};

/**
 * Returns a pricing creating request, which is basically a PricingPreviewRequest
 * with the added customer
 *
 * @param driver - The 'driver item' which right now encapsulates driver and customer data
 * @param car - The car data
 * @param coverages - The list of coverages to apply
 */
export function makePricingCreationRequest({
  customer,
  car,
  productId,
  coverages,
  brands,
  models,
  license,
  packageSelected,
  deductibleOption,
  hasMultipleOptions,
  profileId,
  additionalBrokers,
  units,
  subagentlicense,
}: MakePricingCreationRequestParams): PricingCreationRequest {
  const pricingPreviewRequest = makeUpdatePricingRequest({
    customer,
    car,
    coverages,
    hasMultipleOptions,
    modifiedPackage: -1,
    productId,
    units,
    deductOption: deductibleOption,
    profileId,
  });

  const brandDescription = brandNameForId(car.brand, brands);
  const modelDescription = modelNameForId(car.model.id, models);

  /**
   * If the quotation has "multiple options" and the product matches, make sure the coverages are sent as
   * "empty"
   */
  let sentUnits = pricingPreviewRequest.units;
  const sentHasMultipleOptions =
    hasMultipleOptions || pricingPreviewRequest.productId === THIRD_PARTY_PACK;
  if (pricingPreviewRequest.productId === THIRD_PARTY_PACK) {
    sentUnits = pricingPreviewRequest.units.map((unit) => ({
      ...unit,
      coverages: [],
    }));
  }

  const isNaturalPerson = customer.type === NATURAL_PERSON;

  return {
    productId: pricingPreviewRequest.productId,
    sendEmailCreatorUser: customer.sendEmailCreatorUser,
    notes: '',
    discountPrice: pricingPreviewRequest.discountPrice,
    units: sentUnits,
    customerType: customer.type,

    /** When customerType is NaturalPerson, legalEntity is null and viceversa */
    customer: isNaturalPerson
      ? {
          firstName: customer.firstName,
          lastName: customer.lastName,
          gender: customer.gender,
          address: '',
          birthday: digestDateForBackend(customer.birthday),
          email: customer.email,
          phone: customer.phoneNumber,
        }
      : null,
    legalEntity: !isNaturalPerson
      ? {
          name: customer.entityName,
          email: customer.email,
          phone: customer.phoneNumber,
        }
      : null,
    summary: {
      carBrand: brandDescription,
      carModel: modelDescription,
    },
    brokerLicense: license,
    packageOption: packageSelected,
    hasMultipleOptions: sentHasMultipleOptions,
    profileId: Number(profileId),
    additionalBrokers,
    agencyId: subagentlicense,
    copyOrder: 0,
    parentQuotation: 0,
  };
}

export function makePricingRequestEndorsementCompare(
  quotation: Quotation,
  coverages: QuotationCoverage[],
): PricingRequest {
  const { deductibleOption, unit } = defaultUnit(quotation);

  const quotationCustomer: CustomerItem = {
    ...makeEmptyCustomerItem,
    id: quotation.customer.id,
    type:
      quotation.customerType === NATURAL_PERSON ? NATURAL_PERSON : LEGAL_PERSON,
    sendEmailCreatorUser: quotation.sendEmailCreatorUser,
    entityName:
      quotation.customerType === LEGAL_PERSON
        ? quotation.legalEntity?.name || ''
        : '',
    birthday: dateFromServerString(quotation.customer.birthday),
    firstName: quotation.customer.firstName,
    lastName: quotation.customer.lastName,
    gender: quotation.customer.gender,
    email: quotation.customer.email,
    phoneNumber: quotation.customer.phone,
    transitZone: unit.driver.transitZone,
    transitTime: unit.driver.transitTime,
    collisionsQty: Number(unit.driver.foulsQty),
    exonerationQty: 0,
    offensesQty: 0,
    alcoholBreath: Number(unit.driver.alcoholicBreath),
    driverFirstName: unit.driver.firstName,
    driverLastName: unit.driver.lastName,
    driverBirthday: dateFromServerString(unit.driver.birthday),
    driverGender: unit.driver.gender,
    driverAndCustomerAreDifferent: driverAndCustomerAreDifferent(
      unit.driver,
      quotation.customer,
    ),
    driverIdNumber: '',
    driverIdType: 'N',
  };

  const quotationCar: CarItem = {
    cylinder: '',
    fuel: '',
    hasBill: false,
    passengersQty: '0',
    serialChassis: '',
    serialMotor: '',
    treasuryValue: '0',
    brand: unit.brandId,
    model: {
      id: unit.modelId,
      brand: '',
      name: '',
    },
    year: unit.modelYear,
    value: unit.value,
    isNewCar: unit.isNewCar,
    typeOfMoney: '',
    licensePlate: '',
    use: '',
    category: '',
    color: '',
    noLicensePlate: false,
  };

  return makePricingPreviewRequest(
    quotationCustomer,
    quotationCar,
    quotation.productId,
    coverages,
    deductibleOption,
    quotation.quotationProfile.id,
  );
}
